// The ticket queue. The order is the database's: least time left first, so
// overdue tickets sit at the top in Fleetly crimson, then everything with no
// clock running, newest first.
//
// Rows can be ticked for the backlog: resolve, close or reopen up to 200 at a
// time. Ticking never opens a ticket and opening never ticks one.
import { Inbox as InboxIcon, Mail, MessageCircle, Search, UserRound } from 'lucide-react'
import { Checkbox } from '../../lib/shadcn/checkbox'
import { Input } from '../../lib/shadcn/input'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { cn } from '../../lib/shadcn/utils'
import { clockLabel, filterLabel, statusLabel } from '../../utils/inbox'
import { kindLabel, listTime } from '../../utils/whatsapp'
import type { Options, QueueRow } from './types'

export type InboxView = 'open' | 'mine' | 'unassigned' | 'overdue' | 'waiting' | 'resolved' | 'filtered' | 'all'

const VIEWS: { key: InboxView; label: string }[] = [
  { key: 'open', label: 'Open' },
  { key: 'mine', label: 'Mine' },
  { key: 'unassigned', label: 'Unassigned' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'waiting', label: 'Waiting' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'filtered', label: 'Filtered' },
  { key: 'all', label: 'All' },
]

const TONE_CLASS = {
  overdue: 'bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))]',
  soon: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
  ok: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
  paused: 'bg-muted text-muted-foreground',
  none: 'text-muted-foreground',
} as const

export function QueueList(props: {
  rows: QueueRow[]
  total: number
  loading: boolean
  loaded: boolean
  view: InboxView
  onView: (v: InboxView) => void
  category: string
  onCategory: (c: string) => void
  channel: string
  onChannel: (c: string) => void
  search: string
  onSearch: (s: string) => void
  options: Options | null
  selected: string | null
  onSelect: (id: string) => void
  ticked: Set<string>
  onTick: (id: string, on: boolean) => void
}) {
  const { rows } = props
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="space-y-2 border-b border-border p-3">
        <div className="flex flex-wrap gap-1">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              onClick={() => props.onView(v.key)}
              className={cn(
                'rounded-full px-2.5 py-1 text-xs font-medium',
                props.view === v.key
                  ? 'bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))]'
                  : 'bg-muted text-muted-foreground hover:text-foreground',
              )}
            >
              {v.label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={props.search}
            onChange={(e) => props.onSearch(e.target.value)}
            placeholder="Search subject, sender or #number"
            className="pl-8"
          />
        </div>
        <div className="flex gap-2">
          <NativeSelect value={props.category} onChange={(e) => props.onCategory(e.target.value)} aria-label="Category">
            <option value="">Every category</option>
            {(props.options?.categories ?? []).map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </NativeSelect>
          <NativeSelect value={props.channel} onChange={(e) => props.onChannel(e.target.value)} aria-label="Channel" className="w-36">
            <option value="">Every channel</option>
            <option value="email">Email</option>
            <option value="whatsapp">WhatsApp</option>
          </NativeSelect>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {!props.loaded && props.loading && (
          <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Reading the queue…
          </div>
        )}
        {props.loaded && rows.length === 0 && (
          <div className="flex flex-col items-center gap-2 p-8 text-center text-sm text-muted-foreground">
            <InboxIcon className="h-6 w-6" />
            Nothing here.
          </div>
        )}
        {rows.map((r) => {
          const c = clockLabel(r)
          const who = r.person_name ?? r.requester_name ?? r.requester_email ?? 'Unknown sender'
          return (
            <div
              key={r.id}
              className={cn(
                'flex gap-2 border-b border-border px-3 py-2.5',
                props.selected === r.id ? 'bg-accent' : 'hover:bg-muted/50',
                r.is_overdue && 'border-l-4 border-l-[hsl(var(--brand))]',
              )}
            >
              <Checkbox
                className="mt-1"
                checked={props.ticked.has(r.id)}
                onCheckedChange={(v) => props.onTick(r.id, v === true)}
                aria-label={`Select ticket ${r.ref}`}
              />
              <button type="button" onClick={() => props.onSelect(r.id)} className="min-w-0 flex-1 text-left">
                <div className="flex items-center gap-2">
                  {r.channel === 'whatsapp' ? (
                    <MessageCircle className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  ) : (
                    <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  )}
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{who}</span>
                  <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                    {listTime(r.last_message_at ?? r.first_received_at)}
                  </span>
                </div>
                <p className="truncate text-sm text-foreground">
                  <span className="text-muted-foreground">#{r.ref} </span>
                  {r.subject ?? '(no subject)'}
                </p>
                {r.snippet && <p className="truncate text-xs text-muted-foreground">{r.snippet}</p>}
                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]">
                  <span className={cn('rounded px-1.5 py-0.5 font-medium tabular-nums', TONE_CLASS[c.tone])}>{c.label}</span>
                  <span className="text-muted-foreground">{statusLabel(r.status)}</span>
                  <span className="text-muted-foreground">·</span>
                  <span className={r.contact_kind === 'unknown' ? 'text-muted-foreground italic' : 'text-muted-foreground'}>
                    {r.contact_kind === 'unknown' ? 'Unmatched' : kindLabel(r.contact_kind)}
                  </span>
                  {r.assigned_name && (
                    <span className="inline-flex items-center gap-0.5 text-muted-foreground">
                      <UserRound className="h-3 w-3" /> {r.assigned_name}
                    </span>
                  )}
                  {r.filter_reason && <span className="text-muted-foreground">{filterLabel(r.filter_reason)}</span>}
                </div>
              </button>
            </div>
          )
        })}
        {props.total > rows.length && (
          <p className="p-3 text-center text-xs text-muted-foreground">
            Showing the first {rows.length} of {props.total}. Narrow the view or search to see the rest.
          </p>
        )}
      </div>
    </div>
  )
}
